import type { FetchErrorLike } from '../../../../mini-apps/_shared/fetch-error-types';
import type { ExternalWriteBlockedProblem } from '../../../../src/shared/external-writes/wire';

export type ApiProblemKind = 'http' | 'network' | 'aborted' | 'malformed';

/**
 * Sentence shown whenever Terra itself couldn't be reached — one shared
 * network-failure sentence app-wide (never "Salesforce", never a status
 * code). Equals today's Signals `NETWORK_FALLBACK_TITLE`
 * (`components/flow/signals/useSignalsMutations.ts`).
 */
export const NETWORK_FAILURE_MESSAGE = "Couldn't reach Terra just now. Please try again.";

/**
 * Sentence shown when the server sent no usable text — a non-2xx response
 * whose body isn't a `problem()` envelope, or a 2xx body that failed its own
 * schema. Equals today's Signals `GENERIC_FALLBACK_TITLE`. Never a status
 * code, never the literal string "HTTP".
 */
export const UNEXPECTED_RESPONSE_MESSAGE = 'Something went wrong. Please try again in a moment.';

interface ApiProblemErrorInit {
  kind: ApiProblemKind;
  status: number;
  code: string;
  title?: string;
  detail?: string;
  body?: unknown;
  userMessage?: string;
  cause?: unknown;
  oauthUrl?: string;
  retryAfterSec?: number;
}

/**
 * One error class for every `fetchJson` failure mode — promoted from
 * `components/flow/signals/useSignalsMutations.ts`'s `SignalActionError`
 * (plan `2026-09-25-003-pr1683-premerge-fixes-plan.md`, U3; U4a migrates
 * that module onto this class, U4b migrates the Ranker client). Implements
 * `FetchErrorLike` so `isFetchError`/`classifyError`/`notifyError`
 * (`lib/errors.ts`) and the global `MutationCache` toast keep working for
 * any caller that switches to it.
 *
 * `message` is ALWAYS `userMessage ?? <kind-appropriate fallback>` — never a
 * status code, never the literal string "HTTP" (CLAUDE.md's rule that a rep
 * never sees a raw status). `userMessage` itself is set ONLY when the
 * server actually sent human text; a string-typed `body.error` (a machine
 * code, per `mini-apps/_shared/fetch-error.ts`'s `throwFetchError`) is never
 * surfaced as `userMessage`.
 *
 * Lives in its own module (not `apiFetchJson.ts`, which re-exports it) so
 * `api.ts` can throw it without an `api.ts` <-> `apiFetchJson.ts` cycle.
 */
export class ApiProblemError extends Error implements FetchErrorLike {
  readonly kind: ApiProblemKind;
  readonly status: number;
  readonly code: string;
  readonly title?: string;
  readonly detail?: string;
  readonly body: unknown;
  readonly userMessage?: string;
  /** A Salesforce-reauth (or similar) redirect URL, carried on a 401
   *  `problem()` body's `oauthUrl` field — `lib/errors.ts`'s `notifyError`
   *  already renders a "Reconnect" toast action for any `FetchErrorLike`
   *  with this set; per-surface inline error boxes read it directly to show
   *  their own "Reconnect Salesforce" link. */
  readonly oauthUrl?: string;
  /** Seconds the server asked the client to wait, from a `problem()` body's
   *  `retryAfterSec` field (a 429's own wait). */
  readonly retryAfterSec?: number;
  /** The rejection this error was translated from (a `TypeError` or
   *  `AbortError`/`DOMException`). Declared as an own field rather than via
   *  the ES2022 `Error(message, { cause })` constructor option — this
   *  package's `tsconfig` targets `ES2020`, whose `lib.es5.d.ts` types
   *  `Error`'s constructor as taking only `message?: string` and doesn't
   *  declare `.cause` at all, so passing a second constructor argument or
   *  reading `.cause` off a bare `Error` fails `tsc` even though the
   *  property itself is a genuine runtime feature on modern engines. */
  readonly cause?: unknown;

  constructor(init: ApiProblemErrorInit) {
    const fallback =
      init.kind === 'network' || init.kind === 'aborted' ? NETWORK_FAILURE_MESSAGE : UNEXPECTED_RESPONSE_MESSAGE;
    const message = init.userMessage ?? fallback;
    super(message);
    this.name = 'ApiProblemError';
    this.kind = init.kind;
    this.status = init.status;
    this.code = init.code;
    this.title = init.title;
    this.detail = init.detail;
    this.body = init.body;
    this.userMessage = init.userMessage;
    this.cause = init.cause;
    this.oauthUrl = init.oauthUrl;
    this.retryAfterSec = init.retryAfterSec;
  }
}

export type WriteRefusal = 'read_only_impersonation' | 'external_write_blocked';

type WriteRefusedErrorInit =
  | { refusal: 'read_only_impersonation'; userMessage: string }
  | { refusal: 'external_write_blocked'; userMessage: string; problem: ExternalWriteBlockedProblem };

/**
 * A write the server refused on policy — read-only impersonation (403) or the
 * external-write sandbox (423). `apiFetch` toasts its `userMessage` (fixed
 * when it is thrown) and throws it instead of resolving, because no call site
 * can act on either refusal inline; `notifyError` ignores it.
 */
export class WriteRefusedError extends ApiProblemError {
  readonly refusal: WriteRefusal;
  declare readonly userMessage: string;

  constructor(init: WriteRefusedErrorInit) {
    super(
      init.refusal === 'read_only_impersonation'
        ? { kind: 'http', status: 403, code: 'READ_ONLY_IMPERSONATION', userMessage: init.userMessage }
        : {
            kind: 'http',
            status: 423,
            code: 'EXTERNAL_WRITE_BLOCKED',
            title: init.problem.title,
            body: init.problem,
            userMessage: init.userMessage,
          },
    );
    this.name = 'WriteRefusedError';
    this.refusal = init.refusal;
  }
}
